import {
    Box,
    IconButton,
    Slider,
    Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState, forwardRef } from "react";
import { GenerationIcon } from "../../../../Custom-imeges/SuitesIcons/ImageSuiteIcons/GenerationExamples/GenerationIcon";
import {
    DownloadOutlined,
    Pause,
    PlayArrow,
    ZoomOutMapOutlined,
} from "@mui/icons-material";

const videoContainerStyle = {
    position: "relative",
    width: "100%",
    height: "100%",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

const blurredBackgroundStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 0,
    overflow: "hidden",
    pointerEvents: "none",
};
interface OutputVideoProps {
    videoUrl: string;
    isLoading: boolean;
    generateTime: string;
    isHistory?: boolean;
}

const OutputVideo = forwardRef<HTMLVideoElement, OutputVideoProps>(
    ({ videoUrl, isLoading, generateTime, isHistory }: OutputVideoProps, ref) => {
        const [isPlaying, setIsPlaying] = useState(false);
        const [currentTime, setCurrentTime] = useState(0);
        const [duration, setDuration] = useState(0);
        const [hoverVideo, setHoverVideo] = useState(false);
        const videoRef = useRef<HTMLVideoElement | null>(null);
        useEffect(() => {
            if (typeof ref === "function") {
                ref(videoRef.current);
            } else if (ref && "current" in ref) {
                (ref as React.MutableRefObject<HTMLVideoElement | null>).current =
                    videoRef.current;
            }
        }, [ref]);
        // Force reload video source
        useEffect(() => {
            if (videoRef.current) {
                videoRef.current.load();
            }
        }, [videoUrl]);

        useEffect(() => {
            let startY: any = null;
            const handleTouchStart = (e: TouchEvent) => {
                startY = e.touches[0].clientY;
            };
            const handleTouchEnd = (e: TouchEvent) => {
                const endY = e.changedTouches[0].clientY;
                if (startY && endY - startY > 100) {
                    if (document.fullscreenElement) {
                        document.exitFullscreen();
                    }
                }
                startY = null;
            };

            const video = videoRef.current;
            if (video) {
                video.addEventListener("touchstart", handleTouchStart);
                video.addEventListener("touchend", handleTouchEnd);
            }

            return () => {
                if (video) {
                    video.removeEventListener("touchstart", handleTouchStart);
                    video.removeEventListener("touchend", handleTouchEnd);
                }
            };
        }, []);

        useEffect(() => {
            const video = videoRef.current;
            if (video) {
                const updateTime = () => setCurrentTime(video.currentTime);
                video.addEventListener("timeupdate", updateTime);

                return () => {
                    video.removeEventListener("timeupdate", updateTime);
                };
            }
        }, [isLoading]);

        useEffect(() => {
            const video = videoRef.current;
            if (video) {
                const setVideoDuration = () => setDuration(video.duration);
                const handleVideoEnd = () => setIsPlaying(false);
                video.addEventListener("loadedmetadata", setVideoDuration);
                video.addEventListener("ended", handleVideoEnd);
                return () => {
                    video.removeEventListener("loadedmetadata", setVideoDuration);
                    video.removeEventListener("ended", handleVideoEnd);
                };
            }
        }, [isLoading]);

        const togglePlayPause = () => {
            if (videoRef.current) {
                if (videoRef.current.paused) {
                    videoRef.current
                        .play()
                        .then(() => setIsPlaying(true))
                        .catch((error: string) => {
                            console.error("Error playing video:", error);
                        });
                } else {
                    videoRef.current.pause();
                    setIsPlaying(false);
                }
            }
        };

        const handleSeek = (event: Event, newValue: number | number[]) => {
            if (videoRef.current) {
                videoRef.current.currentTime = newValue as number;
                setCurrentTime(newValue as number);
            }
        };

        const handleFullscreen = () => {
            if (videoRef.current) {
                if (document.fullscreenElement) {
                    document.exitFullscreen();
                } else {
                    videoRef.current.requestFullscreen();
                }
            }
        };

        const handleDownload = (): void => {
            if (!videoUrl) {
                console.error("Video URL is not available.");
                return;
            }
            const downloadLink = document.getElementById(
                "download-link"
            ) as HTMLAnchorElement;
            if (downloadLink) {
                downloadLink.href = videoUrl;
                downloadLink.download = "video.mp4";
                downloadLink.click();
            }
        };

        const formatTime = (time: number) => {
            const minutes = Math.floor(time / 60);
            const seconds = Math.floor(time % 60);
            return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
        };
        const renderVideoLoading = () => (
            <Box className="generate-loading-container">
                <Box className="image-suite-generation-loading">
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <GenerationIcon />
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexDirection: "column",
                            }}
                        >
                            <Typography className="generation-loading-text">
                                Generate
                            </Typography>
                            {generateTime&&
                                <Typography className=" ms-2 time-result-header">
                                    {` Result Time ${generateTime} min `}
                                </Typography>
                            }
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
        const renderGeneratedVideo = () => (
            <Box className="generated-image-container">
                <Box className="generated-image-box">
                    <Box sx={videoContainerStyle}>
                        <Box sx={blurredBackgroundStyle}>
                            {videoUrl && (
                                <video
                                    ref={ref}
                                    src={videoUrl}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        filter: "blur(24px) brightness(0.7)",
                                        pointerEvents: "none",
                                    }}
                                    tabIndex={-1} // Prevents focus
                                    aria-hidden="true"
                                />
                            )}
                        </Box>
                        <video
                            ref={videoRef}
                            className="w-100 h-100"
                            style={{
                                position: "relative",
                                zIndex: 1,
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain", // keeps aspect ratio, adds black/blur sides
                                background: "transparent",
                            }}
                            controlsList="noplaybackrate"
                            disablePictureInPicture
                            controls={isHistory ? false : true}
                        >
                            <source src={videoUrl} type="video/mp4" />
                        </video>
                    </Box>
                    {isHistory && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                zIndex: 1,
                                bottom: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                background:
                                    !isPlaying || hoverVideo
                                        ? "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 38.2%, #000 100%)"
                                        : "none",
                                display: "flex",
                                justifyContent: "flex-end",
                                flexDirection: "column",
                                padding: "0.5rem",
                            }}
                        >
                            {(!isPlaying || (isPlaying && hoverVideo)) && (
                                <>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mt: 0.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                            }}
                                        >
                                            <IconButton
                                                onClick={togglePlayPause}
                                                sx={{ color: "#fff" }}
                                            >
                                                {isPlaying ? <Pause /> : <PlayArrow />}
                                            </IconButton>
                                            <Typography sx={{ color: "#fff", fontSize: "0.875rem" }}>
                                                {formatTime(currentTime)} / {formatTime(duration)}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                            }}
                                        >
                                            <IconButton
                                                onClick={handleFullscreen}
                                                sx={{ color: "#fff" }}
                                            >
                                                <ZoomOutMapOutlined />
                                            </IconButton>
                                            <IconButton onClick={handleDownload} sx={{ color: "#fff" }}>
                                                <DownloadOutlined />
                                                <a id="download-link" style={{ display: "none" }} />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    {/* Time Slider */}
                                    <Slider
                                        value={currentTime}
                                        max={duration}
                                        onChange={handleSeek}
                                        sx={{
                                            color: "#fff",
                                            height: 4,
                                            "& .MuiSlider-thumb": {
                                                width: 12,
                                                height: 12,
                                                backgroundColor: "#fff",
                                            },
                                        }}
                                    />
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        );
        return (
            <Box
                className="generated-Video-ex-container"
                onMouseEnter={() => setHoverVideo(true)}
                onMouseLeave={() => setHoverVideo(false)}
                sx={{
                    border: `1px solid ${isHistory ? "transparent" : "var(--A-background55)"}`,
                    width: isHistory ? "100%" : "98%",
                    height: isHistory ? "100%" : "85%",
                    position: "relative",
                }}
            >
                {isLoading ? renderVideoLoading() : renderGeneratedVideo()}
            </Box>
        );
    });

export default OutputVideo;
